import React, { useEffect, useState, forwardRef } from 'react';
import {
  Typography, Button, CircularProgress, Snackbar, Alert as MuiAlert, Dialog,
  DialogActions, DialogContent, DialogTitle, TextField, Box, Grid, Checkbox, FormControlLabel
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import axiosInstance from '../../api/axiosConfig';
import PropTypes from 'prop-types';

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 2,
  textTransform: 'none',
  fontWeight: 500,
  padding: theme.spacing(1, 3),
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  },
}));

const DetailItemLabel = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  color: theme.palette.text.secondary,
  fontSize: '0.875rem',
  minWidth: '120px',
  marginRight: theme.spacing(1),
}));

const DetailItemValue = styled(Typography)(({ theme }) => ({
  fontWeight: 400,
  color: theme.palette.text.primary,
  fontSize: '0.9rem',
  wordBreak: 'break-word',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.shape.borderRadius,
    '& fieldset': {
      borderColor: theme.palette.grey[400],
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.dark,
      borderWidth: 2,
    },
  },
}));

const Alert = forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const EmployeeBankDetails = ({ employeeId, showSnackbar: parentShowSnackbar }) => {
  const theme = useTheme();
  const [bankDetails, setBankDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [openDialog, setOpenDialog] = useState(false);
  const [currentDetail, setCurrentDetail] = useState(null);
  const [formData, setFormData] = useState({
    accountNumber: '',
    bankName: '',
    ifscCode: '',
    branchName: '',
    accountType: '',
    primaryAccount: false,
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (employeeId && employeeId !== 'unknown') {
      fetchBankDetails();
    } else {
      setLoading(false);
      showSnackbar('Invalid employee ID.', 'error');
    }
    // eslint-disable-next-line
  }, [employeeId]);

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
    if (parentShowSnackbar) {
      parentShowSnackbar(message, severity);
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const fetchBankDetails = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/api/employee-bank-details/${employeeId || 'unknown'}`);
      setBankDetails(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      handleError(error, 'Failed to fetch bank details.');
      setBankDetails([]);
    } finally {
      setLoading(false);
    }
  };

  const handleError = (error, defaultMessage) => {
    let message = defaultMessage;
    if (error.response) {
      message = `Error: ${error.response.data?.message || error.response.statusText || defaultMessage}`;
    } else if (error.request) {
      message = 'Error: No response received from server.';
    } else {
      message = `Error: ${error.message}`;
    }
    console.error("API Error:", error);
    showSnackbar(message, 'error');
  };

  const handleOpenDialog = (detail = null) => {
    setFormErrors({});
    setCurrentDetail(detail);
    setFormData({
      accountNumber: detail?.accountNumber || '',
      bankName: detail?.bankName || '',
      ifscCode: detail?.ifscCode || '',
      branchName: detail?.branchName || '',
      accountType: detail?.accountType || '',
      primaryAccount: detail?.primaryAccount || false,
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentDetail(null);
    setFormErrors({});
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setFormErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.accountNumber.trim()) {
      errors.accountNumber = 'Account Number is required';
    } else if (!/^\d{9,18}$/.test(formData.accountNumber.trim())) {
      errors.accountNumber = 'Account Number must be 9-18 digits';
    }
    if (!formData.bankName.trim()) {
      errors.bankName = 'Bank Name is required';
    }
    if (!formData.ifscCode.trim()) {
      errors.ifscCode = 'IFSC Code is required';
    } else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.ifscCode.trim())) {
      errors.ifscCode = 'Invalid IFSC Code format';
    }
    if (formData.accountType && !['Savings', 'Current', 'Salary'].includes(formData.accountType.trim())) {
      errors.accountType = 'Account Type must be Savings, Current, or Salary';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      if (currentDetail) {
        await axiosInstance.put(`/api/employee-bank-details/${currentDetail.bankDetailId || 'unknown'}`, formData);
        showSnackbar('Bank details updated successfully.', 'success');
      } else {
        await axiosInstance.post('/api/employee-bank-details', { ...formData, employeeId: employeeId || 'unknown' });
        showSnackbar('Bank details added successfully.', 'success');
      }
      fetchBankDetails();
      handleCloseDialog();
    } catch (error) {
      handleError(error, `Failed to ${currentDetail ? 'update' : 'add'} bank details.`);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axiosInstance.delete(`/api/employee-bank-details/${id || 'unknown'}`);
      showSnackbar('Bank details deleted successfully.', 'success');
      fetchBankDetails();
    } catch (error) {
      handleError(error, 'Failed to delete bank details.');
    }
  };

  const renderDetailItem = (label, value) => {
    let displayValue;
    if (value === null || value === undefined || value === '') {
      displayValue = <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>Not Available</Typography>;
    } else {
      displayValue = <DetailItemValue>{value}</DetailItemValue>;
    }

    return (
      <Grid item xs={12} sm={6} md={4}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <DetailItemLabel>{label}:</DetailItemLabel>
          {displayValue}
        </Box>
      </Grid>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  return (
    <div variant="outlined">
    

      {bankDetails.length > 0 ? (
        bankDetails.map((detail) => (
          <Box key={detail.bankDetailId} sx={{ mb: 4, p: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
            <Grid container spacing={3}>
              {renderDetailItem('Account Number', detail.accountNumber)}
              {renderDetailItem('Bank Name', detail.bankName)}
              {renderDetailItem('IFSC Code', detail.ifscCode)}
              {renderDetailItem('Branch Name', detail.branchName)}
              {renderDetailItem('Account Type', detail.accountType)}
              {renderDetailItem('Primary Account', detail.primaryAccount ? 'Yes' : 'No')}
            </Grid>
            <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <StyledButton
                variant="contained"
                color="primary"
                startIcon={<Edit />}
                onClick={() => handleOpenDialog(detail)}
              >
                Edit
              </StyledButton>
              <StyledButton
                variant="outlined"
                color="error"
                startIcon={<Delete />}
                onClick={() => handleDelete(detail.bankDetailId)}
              >
                Delete
              </StyledButton>
            </Box>
          </Box>
        ))
      ) : (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            No bank details available.
          </Typography>
          <StyledButton
            variant="contained"
            color="primary"
            onClick={() => handleOpenDialog()}
          >
            Add Bank Detail
          </StyledButton>
        </Box>
      )}

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
        sx={{ '& .MuiPaper-root': { borderRadius: 3 } }}
      >
        <DialogTitle fontWeight={600}>
          {currentDetail ? 'Edit Bank Detail' : 'Add Bank Detail'}
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <StyledTextField
                label="Account Number *"
                name="accountNumber"
                value={formData.accountNumber}
                onChange={handleChange}
                fullWidth
                error={!!formErrors.accountNumber}
                helperText={formErrors.accountNumber}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <StyledTextField
                label="Bank Name *"
                name="bankName"
                value={formData.bankName}
                onChange={handleChange}
                fullWidth
                error={!!formErrors.bankName}
                helperText={formErrors.bankName}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <StyledTextField
                label="IFSC Code *"
                name="ifscCode"
                value={formData.ifscCode}
                onChange={handleChange}
                fullWidth
                error={!!formErrors.ifscCode}
                helperText={formErrors.ifscCode}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <StyledTextField
                label="Branch Name"
                name="branchName"
                value={formData.branchName}
                onChange={handleChange}
                fullWidth
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <StyledTextField
                label="Account Type"
                name="accountType"
                value={formData.accountType}
                onChange={handleChange}
                fullWidth
                error={!!formErrors.accountType}
                helperText={formErrors.accountType}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.primaryAccount}
                    onChange={handleChange}
                    name="primaryAccount"
                    color="primary"
                  />
                }
                label="Primary Account"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: '20px 24px' }}>
          <StyledButton
            variant="outlined"
            color="secondary"
            onClick={handleCloseDialog}
          >
            Cancel
          </StyledButton>
          <StyledButton
            variant="contained"
            color="primary"
            onClick={handleSubmit}
          >
            {currentDetail ? 'Update' : 'Add'}
          </StyledButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{ width: '100%', borderRadius: 2 }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

EmployeeBankDetails.propTypes = {
  employeeId: PropTypes.string.isRequired,
  showSnackbar: PropTypes.func,
};

export default EmployeeBankDetails;
